const sectionNames = ['FORMAT', 'RUNTIME', 'CAST', 'SCRIPT'] as const;

type ScriptSectionName = (typeof sectionNames)[number];

type ScriptSection = {
  name: ScriptSectionName;
  start: number;
  contentStart: number;
};

const productionCueNames = 'MUSIC|SFX|SOUND|FX|AMBIENCE|AMB|PAUSE|BEAT|DIRECTION|DELIVERY|TRANSITION|NOTE';
const nonSpeakerLabels = new Set(productionCueNames.split('|'));

/**
 * Turns a compact model response into a readable, still fully editable radio
 * script. It only changes whitespace and label placement, then optionally
 * prepends generated metadata; spoken text stays untouched.
 */
export function formatDraftScript(script: string, title?: string, logline?: string): string {
  const normalized = compactWhitespace(title || logline ? removeLeadingMetadata(script) : script);
  const sections = findExpectedSections(normalized);
  const formattedScript = !sections
    ? formatScriptBody(normalized)
    : sections.map((section, index) => {
      const nextSection = sections[index + 1];
      const content = normalized.slice(section.contentStart, nextSection?.start ?? normalized.length).replace(/^,\s*|,\s*$/g, '').trim();

      if (section.name === 'CAST') {
        return `${section.name}:\n${formatCast(content)}`;
      }
      if (section.name === 'SCRIPT') {
        return `${section.name}:\n\n${formatScriptBody(content)}`;
      }
      return `${section.name}: ${content}`;
    }).join('\n\n').trim();

  const metadata = [
    title ? `TITLE: ${formatMetadata(title)}` : '',
    logline ? `LOGLINE: ${formatMetadata(logline)}` : '',
  ].filter(Boolean);
  return metadata.length > 0 ? `${metadata.join('\n\n')}\n\n${formattedScript}` : formattedScript;
}

function compactWhitespace(value: string): string {
  return value
    .replace(/\r\n?/g, '\n')
    .replace(/[\t ]*\n+[\t ]*/g, ' ')
    .replace(/[\t ]{2,}/g, ' ')
    .trim();
}

function removeLeadingMetadata(value: string): string {
  let cleaned = value.trim();
  while (/^(?:TITLE|LOGLINE)\s*:/i.test(cleaned)) {
    cleaned = cleaned.replace(/^(?:TITLE|LOGLINE)\s*:\s*[^\n]*(?:\r?\n+|$)/i, '').trim();
  }
  return cleaned;
}

function formatMetadata(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

function findExpectedSections(script: string): ScriptSection[] | null {
  const sections: ScriptSection[] = [];
  let searchFrom = 0;

  for (const name of sectionNames) {
    const match = new RegExp(`\\b${name}\\s*:`, 'i').exec(script.slice(searchFrom));
    if (!match) return null;

    const start = searchFrom + match.index;
    if (name === 'FORMAT' && start !== 0) return null;

    sections.push({ name, start, contentStart: start + match[0].length });
    searchFrom = start + match[0].length;
  }

  return sections;
}

function formatCast(cast: string): string {
  const characterLabel = `[A-Z][A-Z0-9 &'\-]{0,30}`;
  return cast
    .trim()
    .replace(
      new RegExp(`(^|,\\s*|\\s+)(${characterLabel}):\\s*`, 'g'),
      (_whole, separator: string, label: string) => `${separator ? '\n' : ''}${label} — `,
    )
    .split('\n')
    .map((entry) => entry.trim())
    .filter(Boolean)
    .join('\n');
}

function formatScriptBody(script: string): string {
  let formatted = script.trim();

  formatted = formatted.replace(new RegExp(`\\s*(\\[(?:${productionCueNames})\\s*:[^\\]\\n]*\\])\\s*`, 'gi'), '\n\n$1\n\n');
  formatted = formatted.replace(/(^|[.!?\]])\s*,?\s*([A-Z][A-Z0-9]*(?:[ &'\-][A-Z0-9]+){0,4})\s*:\s*/g, (whole, boundary: string, label: string) => {
    if (nonSpeakerLabels.has(label)) return whole;
    return `${boundary}\n\n${label}:\n`;
  });

  return formatted
    .replace(/[\t ]*\n[\t ]*/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
